import { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { ExploreCatalogCard as ExploreCatalogCardType } from '../../core/types/explore.types'
import ExploreCatalogGrid, { ExploreCatalogGridHandle } from './explore-catalog-grid'

type ExploreCatalogViewProps = {
  cards: ExploreCatalogCardType[]
  onSelectCard: (card: ExploreCatalogCardType) => void
}

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    width: '100%',
    flex: 1,
    minHeight: 0,
    alignItems: 'stretch',
    gap: 12,
  },
  scrollButtonHost: {
    display: 'flex',
    height: '100%',
    width: 48,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollButton: {
    display: 'flex',
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: '#fff',
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    boxShadow: '0 10px 24px rgba(15,23,42,0.14)',
    transition: 'all 200ms ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'var(--color-coral-sender, #E8735A)',
      color: 'var(--color-coral-sender, #E8735A)',
    },
  },
}))

const ExploreCatalogView = ({ cards, onSelectCard }: ExploreCatalogViewProps) => {
  const classes = useStyles()
  const gridRef = useRef<ExploreCatalogGridHandle | null>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isNearTop, setIsNearTop] = useState(true)

  const updateScrollState = useCallback(() => {
    const container = gridRef.current?.stageRef
    if (!container) return

    const { scrollTop, clientHeight, scrollHeight } = container
    const threshold = 12
    setShowScrollButton(scrollHeight - clientHeight > threshold)
    setIsNearTop(scrollTop <= threshold)
  }, [])

  const scrollCards = useCallback(() => {
    const container = gridRef.current?.stageRef
    if (!container) return

    const targetTop = isNearTop ? container.scrollHeight : 0
    container.scrollTo({ top: targetTop, behavior: 'smooth' })
  }, [isNearTop])

  useEffect(() => {
    updateScrollState()
    window.addEventListener('resize', updateScrollState)
    return () => window.removeEventListener('resize', updateScrollState)
  }, [updateScrollState])

  useEffect(() => {
    const container = gridRef.current?.stageRef
    if (!container) return
    container.scrollTo({ top: 0, behavior: 'auto' })
    updateScrollState()
  }, [cards, updateScrollState])

  return (
    <div className={classes.root}>
      <ExploreCatalogGrid
        ref={gridRef}
        cards={cards}
        onScroll={updateScrollState}
        onSelectCard={onSelectCard}
      />

      <div className={classes.scrollButtonHost}>
        {showScrollButton && (
          <button
            type="button"
            className={classes.scrollButton}
            onClick={scrollCards}
            aria-label={isNearTop ? 'Scroll down' : 'Scroll up'}
          >
            <span aria-hidden="true">{isNearTop ? '↓' : '↑'}</span>
          </button>
        )}
      </div>
    </div>
  )
}

export default ExploreCatalogView
